import { useState, useEffect, useContext, createContext } from "react";
import { onAuthStateChanged, onIdTokenChanged, signOut } from "firebase/auth";
import {
  doc,
  onSnapshot,
  collection,
  query,
  limit,
  getDocs,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { resolveOnboardingStatusFromUserDoc } from "../features/auth/userOnboardingStatus.js";

// Create a React Context for auth
const AuthContext = createContext();

// Provider component that wraps app
export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  /** Anonymous ("Try Gift Gaff") sessions — read off the raw Firebase user. */
  const [isGuest, setIsGuest] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [hasGiftees, setHasGiftees] = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  /* Linking a credential to a guest clears `isAnonymous` on the *same* user object
     without re-firing onAuthStateChanged, so track the token instead — otherwise a
     freshly converted account keeps rendering trial UI until a reload. */
  useEffect(
    () =>
      onIdTokenChanged(auth, (user) => {
        setIsGuest(Boolean(user?.isAnonymous));
      }),
    [],
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsGuest(Boolean(user?.isAnonymous));

      if (user) {
        const userProfileRef = doc(db, "users", user.uid);
        const profileUnsubscribe = onSnapshot(
          userProfileRef,
          async (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();
              setUserProfile(data);
              setPhoneVerified(data.phoneVerified ?? false);
              setOnboardingStatus(resolveOnboardingStatusFromUserDoc(data));
              await checkUserHasGiftees(user.uid);
            } else {
              setUserProfile(null);
              setPhoneVerified(false);
              setOnboardingStatus(
                resolveOnboardingStatusFromUserDoc(null),
              );
              setHasGiftees(false);
            }
            setLoading(false);
          },
          (error) => {
            console.error("Error fetching user profile:", error);
            setUserProfile(null);
            setPhoneVerified(false);
            setOnboardingStatus(resolveOnboardingStatusFromUserDoc(null));
            setHasGiftees(false);
            setLoading(false);
          }
        );

        return () => profileUnsubscribe();
      } else {
        setUserProfile(null);
        setPhoneVerified(null);
        setOnboardingStatus(null);
        setHasGiftees(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const checkUserHasGiftees = async (uid) => {
    try {
      const gifteesRef = collection(db, "users", uid, "giftees");
      const q = query(gifteesRef, limit(1));
      const snapshot = await getDocs(q);
      setHasGiftees(!snapshot.empty);
    } catch (error) {
      console.error("Error checking user giftees:", error);
      setHasGiftees(false);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const userWithProfile = currentUser
    ? {
        ...currentUser,
        ...userProfile,
        ...(userProfile
          ? {
              onboardingStatus:
                resolveOnboardingStatusFromUserDoc(userProfile),
            }
          : {}),
      }
    : null;

  return (
    <AuthContext.Provider
      value={{
        currentUser: userWithProfile,
        isGuest,
        hasGiftees,
        phoneVerified,
        onboardingStatus,
        logout,
        checkUserHasGiftees,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
}

// Hook to use auth data anywhere
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  return useContext(AuthContext);
}
